/**
 * LO QUE SE MIRA DE UN REPORTE ANTES DE MANDARLO, que no es de ninguna pantalla en concreto
 * (12-10). Dos cosas que estaban escritas dentro del formulario y contaban mal:
 *
 *   - EL REPORTE CORTO NO SE MANDA VACÍO. El quincenal (y el semanal y el mensual rápido,
 *     que son el mismo formulario) solo miraban el peso: con las estrellas al mínimo y sin
 *     contestar nada más se enviaba sin aviso y el entrenador recibía un peso y nada con lo
 *     que ajustar. Las dos escalas de 0 a 10 del paso 2 son el reporte: van las dos.
 *
 *   - LAS FOTOS DE ESTE REPORTE LAS CUENTA EL SERVIDOR. El resumen decía "Fotos: las tres"
 *     contando las del reporte anterior, y al enviar el servidor contestaba que faltaban
 *     las tres. Se arregló recalculando aquí qué fotos cuentan (por pose y con una ventana
 *     de catorce días escrita otra vez), y esa segunda cuenta no era la del servidor, que
 *     cuenta fotos y mira desde que abre la ventana. Ahora el servidor lo dice en
 *     `GET /reports/formulario` (`fotos_de_este_reporte`, de `routes/reports.py`,
 *     `_fotos_para_este_reporte`) y aquí solo se lee.
 *
 * Puro: ni red ni pantalla.
 */

/**
 * Qué le falta al reporte corto para poder mandarse. `null` si está entero.
 * @param {object} valores los del formulario
 * @returns {string|null} la frase para el cliente
 */
export function faltaEnElReporteCorto(valores) {
    const v = valores || {};
    const sinSensaciones = v.sensaciones_0a10 == null;
    const sinEsfuerzo = v.esfuerzo_resultados == null;
    if (sinSensaciones && sinEsfuerzo) {
        return 'Antes de enviarlo, marca del 0 al 10 tus sensaciones y cuánto te está costando.';
    }
    if (sinSensaciones) return 'Te falta marcar tus sensaciones generales, del 0 al 10.';
    if (sinEsfuerzo) return 'Te falta marcar cuánto te está costando, del 0 al 10.';
    return null;
}

/**
 * EL MENSUAL DEL DOC «REPORTE MENSUAL PLAN PERSONALIZADO» (25-10): una sección cada vez, y no
 * se pasa a la siguiente con algo sin contestar. Las secciones y sus preguntas las manda el
 * servidor (`ficha.secciones_mensual`, de `core/mensual.SECCIONES`) y el servidor lo vuelve a
 * mirar al guardarlo (`core/mensual.respuestas_para_guardar`).
 *
 * Las respuestas van en `valores.respuestas_mensual`: {clave: valor}, y `<clave>_detalle` en
 * las de sí o no y `<clave>_quitar` en la de reducir suplementos.
 *
 * LO QUE SALE DE SUS CHECK-IN NO SE LE PREGUNTA: las estrellas de «¿En qué grado has
 * cumplido...?» y el % del cardio vienen en `grados` (`resumen.grados`, del servidor). Solo si
 * no hay se le pregunta.
 */

/** Lo que contestó a una pregunta del mensual, o lo que sale de sus check-in si es un grado. */
export function respuestaDelMensual(valores, clave, grados) {
    const deCheckin = (grados || {})[clave];
    if (deCheckin != null) return deCheckin;
    return (valores?.respuestas_mensual || {})[clave] ?? null;
}

/** Si esa pregunta se le enseña (sale de sus check-in) en vez de preguntársela. */
export const seLeEnsena = (pregunta, grados) => (
    pregunta.tipo === 'grado' ? (grados || {})[pregunta.clave] != null
        : pregunta.tipo === 'cardio' ? (grados || {}).cardio_pct != null : false);

const _quitar = (valores, clave) => ((valores?.respuestas_mensual || {})[`${clave}_quitar`] || [])
    .map((x) => String(x).trim()).filter(Boolean);

/**
 * Si esa pregunta se calla por lo que CONTESTÓ a otra (28-10: «¿Cuánto te ha costado?» se
 * oculta si a «¿En qué grado has cumplido la dieta?» le puso una estrella). Solo lo que
 * contestó él: lo que sale de sus check-in no la calla. El servidor lo mira igual
 * (`core/mensual.respuestas_para_guardar`, `salvo`).
 */
export const seCalla = (pregunta, valores) => {
    const s = pregunta?.salvo;
    return !!s && (valores?.respuestas_mensual || {})[s.clave] === s.valor;
};

/**
 * Qué le falta a una sección para pasar a la siguiente. `null` si está entera.
 * @param {object} seccion una de `ficha.secciones_mensual`
 * @param {object} valores los del formulario
 * @param {object} grados `resumen.grados`
 * @returns {string|null} la frase para el cliente
 */
export function faltaEnLaSeccion(seccion, valores, grados) {
    const r = valores?.respuestas_mensual || {};
    for (const p of seccion?.preguntas || []) {
        if (seCalla(p, valores)) continue;
        const v = respuestaDelMensual(valores, p.clave, grados);
        if (p.tipo === 'grado' || p.tipo === 'estrellas') {
            if (!Number.isInteger(v) || v < 1 || v > 5) return `Marca las estrellas en «${p.pregunta}».`;
        } else if (p.tipo === 'si_no') {
            if (v !== 'si' && v !== 'no') return `Contesta sí o no a «${p.pregunta}».`;
            if (v === 'si' && !String(r[`${p.clave}_detalle`] || '').trim()) {
                return `Cuéntanos qué en «${p.pregunta}».`;
            }
        } else {
            if (p.tipo === 'cardio' && seLeEnsena(p, grados)) continue;
            if (!(p.opciones || []).some((o) => o.valor === v)) return `Elige una respuesta en «${p.pregunta}».`;
            if (p.tipo === 'objetivo' && v === 'cambiar' && !valores?.proximo_objetivo) {
                return `Dinos cuál en «${p.pregunta_si_cambia}».`;
            }
            if (p.quitar && v === p.quitar.si && !_quitar(valores, p.clave).length) {
                return `Dinos cuáles en «${p.quitar.pregunta}».`;
            }
        }
    }
    // «TU MATERIAL»: sin «Confirmar» desde el 28-10. Pulsar «Continuar» ya es dar las listas
    // por buenas como están.
    return null;
}

/**
 * Lo que viaja en `respuestas_mensual`: lo que contestó en sus secciones, con lo de sus
 * check-in puesto en los grados. Lo que no se le preguntó no viaja.
 * @param {object} valores los del formulario
 * @param {Array} secciones `ficha.secciones_mensual`
 * @param {object} grados `resumen.grados`
 * @returns {object}
 */
export function respuestasDelMensualParaElServidor(valores, secciones, grados) {
    const r = valores?.respuestas_mensual || {};
    const salida = {};
    for (const s of secciones || []) {
        for (const p of s.preguntas || []) {
            if (p.tipo === 'cardio' && seLeEnsena(p, grados)) continue;
            if (seCalla(p, valores)) continue;
            const v = respuestaDelMensual(valores, p.clave, grados);
            if (v == null || v === '') continue;
            salida[p.clave] = v;
            // Lo que sale de sus check-in, marcado: la ficha del equipo dice que no se le preguntó.
            if (p.tipo === 'grado' && seLeEnsena(p, grados)) salida[`${p.clave}_de_checkin`] = true;
            if (p.tipo === 'si_no' && v === 'si') salida[`${p.clave}_detalle`] = String(r[`${p.clave}_detalle`] || '').trim();
            if (p.quitar && v === p.quitar.si) salida[`${p.clave}_quitar`] = _quitar(valores, p.clave);
        }
    }
    return salida;
}

/**
 * LAS CUATRO DE SÍ O NO DEL QUINCENAL (doc «REPORTE QUINCENAL», 24-10). Las preguntas las
 * manda el servidor (`ficha.preguntas_quincenal`, de `core/quincenal.PREGUNTAS`); la quinta,
 * la libre, es la de clave `notes` y va en `valores.notes`.
 *
 * EL «NO» VA MARCADO DE ENTRADA, como en sus maquetas (Francisco, 24-10): una pregunta que no
 * ha tocado es un no.
 * @param {object} valores los del formulario (`respuestas_quincenal`: {clave: {respuesta, detalle}})
 * @param {string} clave la de la pregunta
 * @returns {{respuesta: 'si'|'no', detalle: string}}
 */
export function respuestaDelQuincenal(valores, clave) {
    const r = (valores?.respuestas_quincenal || {})[clave] || {};
    return { respuesta: r.respuesta === 'si' ? 'si' : 'no', detalle: r.detalle || '' };
}

const deSiONo = (preguntas) => (preguntas || []).filter((p) => p && p.clave !== 'notes');

/**
 * Qué le falta al quincenal para mandarse: un «sí» sin decir cuál. `null` si está entero.
 * El servidor lo mira igual (`core/quincenal.respuestas_para_guardar`).
 * @param {object} valores los del formulario
 * @param {Array} preguntas `ficha.preguntas_quincenal`
 * @returns {string|null} la frase para el cliente
 */
export function faltaEnElQuincenal(valores, preguntas) {
    const sinDecir = deSiONo(preguntas).find((p) => {
        const r = respuestaDelQuincenal(valores, p.clave);
        return r.respuesta === 'si' && !r.detalle.trim();
    });
    return sinDecir ? `Cuéntanos cuál en «${sinDecir.pregunta}».` : null;
}

/**
 * Lo que viaja en `respuestas_quincenal`: una por pregunta que se le hizo, y el detalle solo
 * con el sí. La pregunta no viaja: la pone el servidor.
 * @param {object} valores los del formulario
 * @param {Array} preguntas `ficha.preguntas_quincenal`
 * @returns {Array<{clave: string, respuesta: string, detalle: string|null}>}
 */
export function respuestasDelQuincenalParaElServidor(valores, preguntas) {
    return deSiONo(preguntas).map((p) => {
        const r = respuestaDelQuincenal(valores, p.clave);
        return { clave: p.clave, respuesta: r.respuesta,
            detalle: r.respuesta === 'si' ? (r.detalle.trim() || null) : null };
    });
}
